import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Row,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { hexToColorOption } from '../../../utils/colorUtils';
import { useTeachingBlockStore } from '../../TeachingBlockGrid/Contexts/TeachingBlockContext';
import { TeachingBlockVersionTableType } from '../graphql/types';
import { createTeachingBlockVersionTableColumns } from '../utils/TeachingBlockVersionTableUtils';

type TeachingBlockVersionSubjectsTableProps = {
  versionUuid: string | undefined;
  search: string;
  versionSubjectUuids: string[];
  onRowSelection: (rowData: TeachingBlockVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
};

export const TeachingBlockVersionSubjectsTable = observer(
  ({
    versionUuid,
    search,
    versionSubjectUuids,
    onRowSelection,
    onShowLessonClick,
  }: TeachingBlockVersionSubjectsTableProps) => {
    const { t } = useTranslation();

    const { sorting, saveSorting } = useColumnsSort('teachingBlockVersionSubjectsTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

    const columns = useMemo(() => createTeachingBlockVersionTableColumns(t('subject.title', { count: 1 })), [t]);

    const store = useTeachingBlockStore();

    //biome-ignore lint/correctness/useExhaustiveDependencies(t): suppress dependency t
    const createActionItems = useCallback((_row: Row<TeachingBlockVersionTableType>): DropdownMenuItem[] => {
      return [
        {
          label: t('teachingBlockVersion.editData'),
        },
        {
          label: t('teachingBlockVersion.test'),
        },
        {
          label: t('teachingBlockVersion.remove'),
          type: 'default',
          color: 'error',
        },
      ];
    }, []);

    const { subjectData } = useLoadBasicData({ pause: !versionUuid });

    const subjectTableData: TeachingBlockVersionTableType[] = useMemo(() => {
      return (
        subjectData?.subjects
          .filter((s) => versionSubjectUuids.includes(s.uuid))
          .map((s) => {
            const { html, label } = hexToColorOption(s.timetableConfig?.color ?? '');
            return {
              color: {
                color: html,
                label,
              },
              count: 0,
              name: s.name,
              shortName: s.shortName,
              uuid: s.uuid,
            };
          }) ?? []
      );
    }, [subjectData?.subjects, versionSubjectUuids]);

    return (
      <Table<TeachingBlockVersionTableType>
        globalFilter={search}
        showBorderRadius
        showShadow
        canScroll
        minHeight={900}
        sorting={sorting}
        onSortingChange={saveSorting}
        columns={columns}
        data={subjectTableData}
        lastColWidth='140px'
        lastCol={(row) => {
          return (
            <ButtonGroup>
              <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                {t('teachingBlockVersion.showLessons')}
              </Button>
              <Dropdown
                noPadding
                disabled={store.readonly}
                trigger={
                  <Button
                    disabled={store.readonly}
                    hierarchy='secondary'
                    icon={<DotsHorizontalIcon className='small' />}
                  />
                }
              >
                <DropdownMenu data={createActionItems(row)} />
              </Dropdown>
            </ButtonGroup>
          );
        }}
        showRowHover
        onRowClick={(_event, row) => {
          if (onRowSelectionChange) onRowSelectionChange({ [row.id]: true });
          onRowSelection(row.original, row.index);
          document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        rowSelection={rowSelection}
        showSelected
      />
    );
  },
);
